import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBook,
  FaBookReader,
  FaBrain,
  FaCalculator,
  FaChild,
  FaGraduationCap,
  FaImage,
  FaLanguage,
  FaLock,
  FaMagic,
  FaMoon,
  FaRegCommentDots,
  FaRegEye,
  FaRoute,
  FaSeedling,
  FaShieldAlt,
  FaStar,
  FaTruck,
  FaUsers
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import '../styles/features.css';

const asArray = (value) => (Array.isArray(value) ? value : []);

const supportIcons = [
  FaGraduationCap,
  FaUsers,
  FaMoon,
  FaStar,
  FaBrain,
  FaLanguage,
  FaCalculator,
  FaRegCommentDots
];
const processIcons = [FaRegCommentDots, FaMagic, FaRegEye];
const deliverableIcons = [FaBook, FaChild, FaTruck, FaImage];
const parentControlIcons = [FaRegEye, FaImage, FaLock, FaShieldAlt];
const ageIcons = [FaSeedling, FaRoute, FaBookReader, FaBrain];
const PROCESS_BOOK_IMAGE_SRC = '/images/book-covers/ali_real_book.png';

const FeaturesPage = () => {
  const { t } = useTranslation();

  const heroHighlights = asArray(t('featuresPage.heroHighlights', { returnObjects: true })).slice(0, 4);
  const supportAreas = asArray(t('featuresPage.supportAreas', { returnObjects: true }));
  const process = t('featuresPage.process', { returnObjects: true });
  const processSteps = asArray(process.steps).slice(0, 3);
  const processTransformation = process.transformation || {};
  const deliverables = t('featuresPage.deliverables', { returnObjects: true });
  const deliverableItems = asArray(deliverables.items).slice(0, 4);
  const parentControls = t('featuresPage.parentControls', { returnObjects: true });
  const parentControlCards = asArray(parentControls.cards).slice(0, 4);
  const ageGroups = asArray(t('featuresPage.ageGroups', { returnObjects: true })).slice(0, 4);

  return (
    <div className="features-page">
      <section className="features-hero">
        <div className="container features-hero-grid">
          <div className="features-hero-copy">
            <h1>{t('featuresPage.heroTitle')}</h1>
            <p>{t('featuresPage.heroSubtitle')}</p>
            <div className="features-actions">
              <Link to="/create-story" className="features-primary-cta">
                {t('featuresPage.heroPrimaryCta')}
                <FaArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>

          <aside className="features-hero-panel" aria-label={t('featuresPage.heroPanelLabel')}>
            <div className="features-hero-panel-top">
              <FaBook aria-hidden="true" />
              <span>{t('featuresPage.heroPanelTitle')}</span>
            </div>
            <ul>
              {heroHighlights.map((item) => (
                <li key={item}>
                  <FaStar aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <section className="features-section features-support-section">
        <div className="container">
          <div className="features-section-header">
            <h2>{t('featuresPage.supportAreasTitle')}</h2>
            <p>{t('featuresPage.supportAreasSubtitle')}</p>
          </div>

          <div className="features-support-grid">
            {supportAreas.map((area, index) => {
              const SupportIcon = supportIcons[index] || FaRegCommentDots;
              const isCustomTopic = index === supportAreas.length - 1;

              return (
                <article
                  key={area.title}
                  className={`features-support-card${isCustomTopic ? ' features-support-card-custom' : ''}`}
                >
                  <span className="features-support-icon" aria-hidden="true">
                    <SupportIcon />
                  </span>
                  <h3>{area.title}</h3>
                  <p>{area.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="features-section features-process-section">
        <div className="container">
          <div className="features-section-header features-process-header">
            <p className="features-kicker">{process.eyebrow}</p>
            <h2>{process.title}</h2>
          </div>

          <div className="features-process-layout">
            <div className="features-process-left">
              <div className="features-process-steps">
                {processSteps.map((step, index) => {
                  const ProcessIcon = processIcons[index] || FaRegEye;

                  return (
                    <article key={step.number} className="features-process-card">
                      <span className="features-process-icon" aria-hidden="true">
                        <ProcessIcon />
                      </span>
                      <span className="features-process-number">{step.number}</span>
                      <span className="features-process-copy">
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </span>
                    </article>
                  );
                })}
              </div>
            </div>

            <aside className="features-process-transform-card" aria-label={processTransformation.previewLabel}>
              <div className="features-process-transform-flow">
                <div className="features-process-need-note">
                  <span>{processTransformation.needLabel}</span>
                  <p>{processTransformation.needQuote}</p>
                </div>
                <div className="features-process-flow-arrow" aria-hidden="true">↓</div>
                <p className="features-process-flow-copy">{processTransformation.connector}</p>
              </div>
              <figure className="features-process-book-photo">
                <img
                  src={PROCESS_BOOK_IMAGE_SRC}
                  alt={processTransformation.imageAlt}
                  loading="lazy"
                />
              </figure>
            </aside>
          </div>
        </div>
      </section>

      <section className="features-section features-deliverables-section">
        <div className="container features-deliverables-layout">
          <div className="features-deliverables-copy">
            <p className="features-kicker">{deliverables.eyebrow}</p>
            <h2>{deliverables.title}</h2>
            <p>{deliverables.subtitle}</p>
          </div>

          <div className="features-deliverables-list">
            {deliverableItems.map((item, index) => {
              const DeliverableIcon = deliverableIcons[index] || FaBook;

              return (
                <article key={item.title} className="features-deliverable-row">
                  <span className="features-row-icon" aria-hidden="true">
                    <DeliverableIcon />
                  </span>
                  <span>
                    <strong>{item.title}</strong>
                    <small>{item.description}</small>
                  </span>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="features-section features-parent-section">
        <div className="container">
          <div className="features-section-header">
            <h2>{parentControls.title}</h2>
            <p>{parentControls.subtitle}</p>
          </div>

          <div className="features-parent-grid">
            {parentControlCards.map((card, index) => {
              const ParentIcon = parentControlIcons[index] || FaShieldAlt;

              return (
                <article key={card.title} className="features-parent-card">
                  <span className="features-card-icon" aria-hidden="true">
                    <ParentIcon />
                  </span>
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="features-section features-age-section">
        <div className="container">
          <div className="features-section-header">
            <h2>{t('featuresPage.ageGroupsTitle')}</h2>
            <p>{t('featuresPage.ageGroupsSubtitle')}</p>
          </div>

          <div className="features-age-grid">
            {ageGroups.map((group, index) => {
              const AgeIcon = ageIcons[index] || FaChild;
              const signals = asArray(group.signals).slice(0, 3);

              return (
                <article key={group.badge} className={`features-age-card features-age-card-${index + 1}`}>
                  <div className="features-age-card-top">
                    <span className="features-age-badge">{group.badge}</span>
                    <span className="features-age-icon" aria-hidden="true">
                      <AgeIcon />
                    </span>
                  </div>
                  <div className={`features-age-preview features-age-preview-${index + 1}`} aria-label={group.previewLabel}>
                    <span className="features-age-preview-label">{group.previewLabel}</span>
                    <div className="features-age-preview-art" aria-hidden="true">
                      <span className="features-age-preview-piece features-age-preview-piece-1" />
                      <span className="features-age-preview-piece features-age-preview-piece-2" />
                      <span className="features-age-preview-piece features-age-preview-piece-3" />
                      <span className="features-age-preview-piece features-age-preview-piece-4" />
                      <span className="features-age-preview-piece features-age-preview-piece-5" />
                    </div>
                  </div>
                  <h3>{group.title}</h3>
                  <p>{group.summary || group.description}</p>
                  {signals.length > 0 && (
                    <ul className="features-age-signals">
                      {signals.map((signal) => (
                        <li key={signal}>{signal}</li>
                      ))}
                    </ul>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="features-final-cta">
        <div className="container">
          <div className="features-final-cta-inner">
            <h2>{t('featuresPage.ctaTitle')}</h2>
            <p>{t('featuresPage.ctaSubtitle')}</p>
            <Link to="/create-story" className="features-primary-cta">
              {t('featuresPage.ctaButton')}
              <FaArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;
